import {
  ChangeEvent,
  useCallback,
  useLayoutEffect,
  useMemo,
  Ref,
  RefCallback,
  MutableRefObject,
  KeyboardEventHandler,
  useState,
} from 'react';

import {
  useCombobox,
  UseComboboxState,
  UseComboboxStateChange,
  UseComboboxStateChangeOptions,
} from 'downshift';

import { mergeRefs, useForwardedRef } from '@adsk/alloy-react-helpers';
import { useOverlay } from '@adsk/alloy-react-overlay';

import { useGroupFilter } from './index';
import useDropdownId from './useDropdownId';
import useFilteredItems from './useFilteredItems';
import useGroupedItems from './useGroupedItems';
import useKeepMenuOpenOnBlur from './useKeepMenuOpenOnBlur';
import useVirtualProps from './useVirtualProps';
import {
  checkReferenceEquality,
  defaultIsItemDisabled,
  defaultLabelGetter,
  getAriaProps,
  isGroupHeader,
  wrapGetItemProps,
} from '../helpers';
import { getSingleSelectionHandlers } from '../helpers/sharedSingleSelection';
import {
  AutocompleteGetMenuProps,
  GenericItem,
  GetInputProps,
  GetItemProps,
  GetToggleButtonProps,
  GroupHeader,
  InputItemGroup,
  UseAutocompleteProps,
} from '../types';
import useCollapsibleGroups from './useCollapsibleGroups';
import useIMEComposition from './useIMEComposition';

/**
 * A hook used to manage all logic needed for an Autocomplete (searchable) dropdown.
 */
const useAutocomplete = <
  TItem extends GenericItem,
  TTarget extends HTMLElement = HTMLDivElement,
>({
  id,
  items,
  groupedItems,
  itemToString = defaultLabelGetter,
  defaultHighlightedIndex = 0,
  target,
  overlayRef: overlayRefProp,
  onInputValueChange: onInputValueChangeProp,
  onIsOpenChange: onIsOpenChangeProp,
  layoutDependency,
  getFilteredItems,
  getMenuProps: getMenuPropsProp,
  initialInputValue,
  stateReducer: stateReducerProp,
  inputRef: inputRefProp,
  isItemDisabled = defaultIsItemDisabled,
  inputValue: inputValueProp,
  ...props
}: UseAutocompleteProps<TItem, TTarget>) => {
  const maybeGroupedItems = items || groupedItems;
  checkReferenceEquality({ ...props, items: maybeGroupedItems });

  const ariaProps = getAriaProps(props);
  const {
    allowCollapsibleGroups,
    showGroupItemCount,
    allowGroupFiltering,
    showEmptyGroups,
  } = props;

  const dropdownId = useDropdownId(id);
  const inputRef = useForwardedRef(inputRefProp || null);
  const targetRef = useForwardedRef(target || null);
  const overlayRef = useForwardedRef(overlayRefProp || null);
  const [expandedGroupKey, setExpandedGroupKey] = useState<string>('group0');
  const [selectedGroupKey, setSelectedGroupKey] = useState<string>();

  const {
    handleCompositionStart,
    getOnCompositionEnd,
    onInputValueChange,
    inputValue,
    setInputValue,
  } = useIMEComposition({
    inputValueProp,
    initialInputValue,
    onInputValueChangeProp,
  });

  const { flatItems, indexGetter, getMenuItems } = useGroupedItems({
    maybeGroupedItems,
    allowCollapsibleGroups,
    expandedGroupKey,
    selectedGroupKey,
  });

  const [isFilteringActive, setIsFilteringActive] = useState(
    Boolean(initialInputValue),
  );

  const { filteredItems } = useFilteredItems<TItem>({
    items: flatItems,
    getFilteredItems,
    itemToString,
    inputValue: isFilteringActive ? inputValue : '',
  });

  const { filteredGroupItems, showGroupHeaders } = useGroupFilter({
    items: filteredItems,
    maybeGroupedItems,
    selectedGroupKey,
  });

  const visibleItems =
    isFilteringActive || selectedGroupKey ? filteredGroupItems : flatItems;

  const { onKeyDown: controlOnKeyDown, getShouldKeepMenuOpenOnBlur } =
    useKeepMenuOpenOnBlur({ id: dropdownId });

  const autocompleteStateReducer = useCallback(
    (
      state: UseComboboxState<TItem>,
      { changes, type }: UseComboboxStateChangeOptions<TItem>,
    ): Partial<UseComboboxState<TItem>> => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          if (
            changes.selectedItem &&
            changes.highlightedIndex !== undefined &&
            isItemDisabled(changes.selectedItem, changes.highlightedIndex)
          ) {
            // TODO: this is a workaround for an issue in downshift that is fixed in V9
            // When we upgrade to V9, we should remove this
            // https://jira.autodesk.com/browse/DSGN-1698
            // https://github.com/downshift-js/downshift/pull/1587#issuecomment-2033855942
            // https://autodesk.slack.com/archives/C5NF41XDW/p1720528384756219
            return state;
          }

          return changes;
        case useCombobox.stateChangeTypes.InputClick:
          if (state.isOpen) {
            setIsFilteringActive(inputValue.length > 0);

            // if the user clicks on the input while the menu is open, then we should keep the menu open with its highlighted index
            return {
              ...changes,
              highlightedIndex: state.highlightedIndex,
              isOpen: true,
            };
          }

          return changes;
        case useCombobox.stateChangeTypes.InputChange:
          if (changes.inputValue === undefined) {
            return changes;
          }

          setIsFilteringActive(true);

          if (changes.inputValue === '') {
            // if the input value is empty, then we should clear the selected item
            return { ...changes, selectedItem: null };
          }

          if (changes.inputValue.length === 1 && state.inputValue.length > 2) {
            // This happens when users select-all and type a new character
            // This causes the minorest of bugs where if you select all but one character and delete it, it will clear the selected value
            return { ...changes, selectedItem: null };
          }

          return changes;
        case useCombobox.stateChangeTypes.FunctionOpenMenu:
          if (changes.isOpen && state.selectedItem) {
            // if the user is opening the menu while an item it selected, then let's make sure
            // that the highlighted index is set to the selected index
            return {
              ...changes,
              highlightedIndex: visibleItems.indexOf(state.selectedItem),
              inputValue: state.selectedItem
                ? itemToString(state.selectedItem)
                : undefined,
            };
          }

          return changes;
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
          setIsFilteringActive(false);

          return {
            ...changes,
            isOpen: false,
            inputValue: itemToString(state.selectedItem),
          };
        case useCombobox.stateChangeTypes.InputBlur: {
          const shouldKeepMenuOpen = getShouldKeepMenuOpenOnBlur();

          if (!shouldKeepMenuOpen) {
            setIsFilteringActive(false);
          }

          return {
            ...changes,
            isOpen: shouldKeepMenuOpen,
            // if the user is closing the menu while an item it selected, then let's make sure
            // that input value is reset to the selected item, discarding their incomplete search.
            // If there is no selected item, then we should set the input value to the empty string
            inputValue: itemToString(state.selectedItem),
          };
        }
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.FunctionSelectItem:
          setIsFilteringActive(false);
          return changes;
        case useCombobox.stateChangeTypes.FunctionCloseMenu:
        case useCombobox.stateChangeTypes.ToggleButtonClick:
          if (!changes.isOpen) {
            setIsFilteringActive(false);

            return {
              ...changes,
              // if the user is closing the menu while an item it selected, then let's make sure
              // that input value is reset to the selected item, discarding their incomplete search.
              // If there is no selected item, then we should set the input value to the empty string
              inputValue: itemToString(state.selectedItem),
            };
          }

          return changes;
        default:
          return changes;
      }
    },
    [
      isItemDisabled,
      itemToString,
      inputValue.length,
      visibleItems,
      getShouldKeepMenuOpenOnBlur,
    ],
  );

  const onIsOpenChange = useCallback(
    (args: UseComboboxStateChange<TItem>) => {
      if (!args.isOpen && !args.selectedItem) {
        setInputValue('');
      }

      onIsOpenChangeProp?.(args);
    },
    [onIsOpenChangeProp, setInputValue],
  );

  const stateReducer = useCallback(
    (
      state: UseComboboxState<TItem>,
      actionAndChanges: UseComboboxStateChangeOptions<TItem>,
    ) => {
      const updatedState = autocompleteStateReducer(state, actionAndChanges);
      const customUpdatedState = stateReducerProp?.(state, actionAndChanges);
      return {
        ...actionAndChanges.changes,
        ...updatedState,
        ...customUpdatedState,
      };
    },
    [autocompleteStateReducer, stateReducerProp],
  );

  const {
    isOpen: downshiftIsMenuOpen,
    getInputProps: rawGetInputProps,
    getItemProps: rawGetItemProps,
    getToggleButtonProps,
    selectItem,
    getMenuProps: rawGetMenuProps,
    openMenu,
    highlightedIndex,
    selectedItem,
    ...useComboboxReturnValue
  } = useCombobox<TItem>({
    id: dropdownId,
    items: visibleItems,
    itemToString,
    initialInputValue,
    onInputValueChange,
    onIsOpenChange,
    defaultHighlightedIndex,
    stateReducer,
    isItemDisabled,
    ...props,
  });

  const {
    show: useOverlayIsOpen,
    targetProps,
    overlayProps,
  } = useOverlay({
    placement: 'bottom-start',
    show: downshiftIsMenuOpen,
    onHide: useComboboxReturnValue.closeMenu,
    target: targetRef,
    overlayRef,
    layoutDependency,
    isOverlayConditionallyRendered: false,
    ...props,
  });

  const getMenuProps: AutocompleteGetMenuProps = useCallback(
    (options, otherOptions) => {
      const temp = getMenuPropsProp
        ? rawGetMenuProps(
            getMenuPropsProp({ ...overlayProps, ...options }, otherOptions),
            otherOptions,
          )
        : rawGetMenuProps({ ...overlayProps, ...options }, otherOptions);

      return temp;
    },
    [getMenuPropsProp, overlayProps, rawGetMenuProps],
  ) as AutocompleteGetMenuProps;

  const getInputProps = useCallback<GetInputProps>(
    ({ ref: refProp, onKeyDown: onKeyDownProp, ...options } = {}) => {
      const combinedRef: RefCallback<HTMLInputElement> = (element) => {
        if (!element) {
          return;
        }

        if (!refProp || typeof refProp === 'string') {
          // do nothing, we can't use a string ref
        } else if (typeof refProp === 'function') {
          refProp(element);
        } else if (typeof refProp === 'object') {
          (refProp as MutableRefObject<HTMLInputElement>).current = element;
        }

        inputRef.current = element;
      };

      const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
        controlOnKeyDown(e);

        onKeyDownProp?.(e);
      };

      const {
        onChange: rawOnChange,
        ref,
        ...inputProps
      } = rawGetInputProps({ ...options, onKeyDown, ref: combinedRef });

      const onChange = (event: ChangeEvent<HTMLInputElement>) => {
        setInputValue(event?.target?.value);
        rawOnChange?.(event);
      };

      return {
        onChange,
        onCompositionStart: handleCompositionStart,
        onCompositionEnd: getOnCompositionEnd({ onChangeProp: rawOnChange }),
        ref: ref as Ref<HTMLInputElement>,
        ...inputProps,
      };
    },
    [
      controlOnKeyDown,
      getOnCompositionEnd,
      handleCompositionStart,
      inputRef,
      rawGetInputProps,
      setInputValue,
    ],
  );

  type MenuItems = typeof groupedItems extends InputItemGroup<TItem>[]
    ? (GroupHeader | TItem)[]
    : TItem[];

  const menuItems = useMemo(() => {
    // This is a required extra step to get proper menu item counts from
    // filteredItems which are all filtered items but not group filtered.
    // The group filter requires these counts to ensure group filter buttons
    // behave correctly
    getMenuItems(filteredItems);
    return getMenuItems(visibleItems, false) as MenuItems;
  }, [getMenuItems, filteredItems, visibleItems]);

  const defaultIndexGetter = useCallback(
    (item: TItem) => menuItems.indexOf(item),
    [menuItems],
  );

  const getItemProps: GetItemProps<TItem> = useCallback(
    (args) =>
      wrapGetItemProps({
        getItemProps: rawGetItemProps,
        highlightedIndex,
        selectedItem,
        indexGetter: indexGetter || defaultIndexGetter,
      })(args),
    [
      defaultIndexGetter,
      highlightedIndex,
      indexGetter,
      rawGetItemProps,
      selectedItem,
    ],
  );

  useLayoutEffect(() => {
    mergeRefs(targetProps.ref, targetRef);
  }, [targetProps, targetRef]);

  const virtualProps = useVirtualProps({
    menuItems,
    isGroupHeader,
    showEmptyGroups,
    showGroupHeaders,
  });

  const singleSelectionHandlers = getSingleSelectionHandlers({
    id: dropdownId,
    selectItem,
    closeMenu: useComboboxReturnValue.closeMenu,
    refToFocusOnClear: inputRef,
    selectedItem,
  });

  const { setExpandedGroup } = useCollapsibleGroups({
    allowCollapsibleGroups,
    flatItems: visibleItems,
    maybeGroupedItems,
    highlightedIndex,
    menuItems,
    expandedGroupKey,
    setExpandedGroupKey,
  });

  return {
    controlProps: {
      ref: targetRef,
      isAutocomplete: true,
      isOpen: useOverlayIsOpen,
      getInputProps,
      getToggleButtonProps: getToggleButtonProps as GetToggleButtonProps,
      openMenu,
      selectedItem,
      itemToString,
      selectItem: selectItem as (item: TItem | null) => void,
      ...(props['data-testid'] ? { 'data-testid': props['data-testid'] } : {}),
      ...ariaProps,
    },
    menuProps: {
      isOpen: useOverlayIsOpen,
      isAutocomplete: true,
      menuItems,
      getItemProps,
      getMenuProps,
      highlightedIndex,
      selectedItem,
      width: targetRef.current?.offsetWidth,
      selectItem,
      isGroupHeader,
      allowCollapsibleGroups,
      allowGroupFiltering,
      showGroupItemCount,
      groupedItems: maybeGroupedItems,
      setExpandedGroupKey: setExpandedGroup,
      expandedGroupKey,
      selectedGroupKey,
      setSelectedGroupKey,
      showGroupHeaders,
      setHighlightedIndex: useComboboxReturnValue.setHighlightedIndex,
      refToFocusOnClick: inputRef,
      showEmptyGroups,
      dropdownId,
      ...singleSelectionHandlers.menuProps,
    },
    /**
     * Props to be provided to `@tanstack/react-virtual`'s `useVirtual` hook when using virtualization.
     */
    virtualProps,
    selectItem,
    ...useComboboxReturnValue,
  };
};

export default Object.assign(useAutocomplete, {
  stateChangeTypes: useCombobox.stateChangeTypes,
});
